import { useCallback, useRef, useState } from 'react';
import { saveUploadReview, type InspectionCapture, type InspectionFlag } from '../api/inspections';

/**
 * How both review pages save a capture's flags and note (the owner,
 * 2026-09-30: "sometimes the text is not saved"). Two things made a note
 * vanish at random:
 *
 * - A note typed and then a flag clicked sent two saves at once, the first
 *   with the note and the second with the flags and the note the page still
 *   held from before, empty; whichever the server wrote last won.
 * - Each answer then wrote the flags and note it carried over the page, so an
 *   early answer could put back what a later click had changed.
 *
 * So what a person did is on the page at once (the next click builds on it,
 * not on the last answer), the saves go to the server one after another in
 * the order they were made, and an answer only says who reviewed and when.
 */
export function useCaptureSave(
  patch: (uploadId: number, change: Partial<InspectionCapture>) => void,
  onError: (message: string) => void,
  enabled: boolean,
) {
  const [savingId, setSavingId] = useState<number | null>(null);
  const tail = useRef<Promise<unknown>>(Promise.resolve());

  const save = useCallback(
    (uploadId: number, flags: InspectionFlag[], note: string) => {
      if (!enabled) return;
      patch(uploadId, { flags, note });
      setSavingId(uploadId);
      const run = tail.current.then(() => saveUploadReview(uploadId, flags, note));
      tail.current = run.catch(() => undefined);
      run
        .then((review) =>
          patch(uploadId, { reviewedBy: review.reviewedBy, reviewedAt: review.reviewedAt }),
        )
        .catch((e) => onError(String(e)))
        .finally(() => setSavingId((id) => (id === uploadId ? null : id)));
    },
    [enabled, patch, onError],
  );

  return { save, savingId };
}
